import type { Lead } from '@up2stay/up2promo-api-client';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type { LeadDuplicateResolutionLabels } from '@/core/components/leads/lead-duplicate-resolution-dialog';
import {
  triggerErrorHaptic,
  triggerSuccessHaptic,
} from '@/core/lib/dnd-haptics';
import {
  findLeadDuplicates,
  type LeadDuplicateMatch,
} from '@/core/lib/find-lead-duplicates';
import {
  createLeadDirect,
  LeadDuplicateConflictError,
  type LeadIncomingData,
  replaceExistingLead,
  supplementExistingLead,
} from '@/core/lib/lead-duplicate-actions';

interface UseLeadDuplicateResolutionOptions {
  existingLeads: Lead[];
  labels: LeadDuplicateResolutionLabels & {
    supplementSuccess: string;
    replaceSuccess: string;
    createNewSuccess?: string;
    useExistingSuccess?: string;
    actionFailed: string;
  };
  getStatusLabel: (status: string) => string;
  onResolved: (leadId?: string) => void;
}

export function useLeadDuplicateResolution({
  existingLeads,
  labels,
  getStatusLabel,
  onResolved,
}: UseLeadDuplicateResolutionOptions) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState<
    LeadDuplicateMatch[]
  >([]);
  const [pendingIncoming, setPendingIncoming] =
    useState<LeadIncomingData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const resetDuplicateState = useCallback(() => {
    setDialogOpen(false);
    setDuplicateMatches([]);
    setPendingIncoming(null);
  }, []);

  const attemptCreate = useCallback(
    async (incoming: LeadIncomingData): Promise<string | undefined> => {
      const matches = findLeadDuplicates(existingLeads, incoming);
      if (matches.length > 0) {
        setPendingIncoming(incoming);
        setDuplicateMatches(matches);
        setDialogOpen(true);
        return undefined;
      }

      try {
        return await createLeadDirect(incoming);
      } catch (error) {
        if (
          error instanceof LeadDuplicateConflictError &&
          error.leads.length > 0
        ) {
          const serverMatches = findLeadDuplicates(error.leads, incoming);
          setPendingIncoming(incoming);
          setDuplicateMatches(
            serverMatches.length > 0
              ? serverMatches
              : error.leads.map((lead) => ({
                  lead,
                  reasons: ['email'] as LeadDuplicateMatch['reasons'],
                })),
          );
          setDialogOpen(true);
          return undefined;
        }
        throw error;
      }
    },
    [existingLeads],
  );

  const runDuplicateAction = useCallback(
    async (
      action: 'use' | 'supplement' | 'replace' | 'create',
      leadId?: string,
    ): Promise<void> => {
      if (!pendingIncoming) return;

      try {
        setIsProcessing(true);

        if (action === 'create') {
          const createdId = await createLeadDirect(pendingIncoming, {
            allowDuplicate: true,
          });
          void triggerSuccessHaptic();
          resetDuplicateState();
          onResolved(createdId);
          return;
        }

        if (!leadId) return;
        const existing = duplicateMatches.find(
          (m) => m.lead.id === leadId,
        )?.lead;
        if (!existing) return;

        if (action === 'use') {
          void triggerSuccessHaptic();
          resetDuplicateState();
          onResolved(leadId);
          return;
        }

        const resolvedId =
          action === 'supplement'
            ? await supplementExistingLead(leadId, existing, pendingIncoming)
            : await replaceExistingLead(leadId, pendingIncoming, existing);

        void triggerSuccessHaptic();
        resetDuplicateState();
        onResolved(resolvedId);
      } catch (error) {
        console.error('Failed to resolve lead duplicate:', error);
        void triggerErrorHaptic();
        toast.error(labels.actionFailed);
      } finally {
        setIsProcessing(false);
      }
    },
    [
      duplicateMatches,
      labels.actionFailed,
      onResolved,
      pendingIncoming,
      resetDuplicateState,
    ],
  );

  const handleDiscard = useCallback(() => {
    resetDuplicateState();
  }, [resetDuplicateState]);

  const dialogProps = useMemo(
    () => ({
      open: dialogOpen,
      onOpenChange: (open: boolean) => {
        if (!open && !isProcessing) {
          resetDuplicateState();
        }
      },
      matches: duplicateMatches,
      labels,
      getStatusLabel,
      isProcessing,
      onSupplement: (leadId: string) =>
        void runDuplicateAction('supplement', leadId),
      onReplace: (leadId: string) => void runDuplicateAction('replace', leadId),
      onUseExisting: (leadId: string) => void runDuplicateAction('use', leadId),
      onCreateNew: () => void runDuplicateAction('create'),
      onDiscard: handleDiscard,
      showUseExisting: true,
      showCreateNew: true,
    }),
    [
      dialogOpen,
      duplicateMatches,
      getStatusLabel,
      handleDiscard,
      isProcessing,
      labels,
      resetDuplicateState,
      runDuplicateAction,
    ],
  );

  return {
    attemptCreate,
    dialogProps,
    isProcessing,
  };
}
